import { useMemo, useState } from 'react';

import { Grid, Typography, useTheme } from '@mui/material';
import { Formik } from 'formik';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useQuery } from '@redwoodjs/web';

import Loader from 'src/components/Loader/Loader';
import TableList from 'src/components/TableList/TableList';
import { showErrorToast } from 'src/components/Toasts/toast';
import StatusChip from 'src/components/UserStatusChip/UserStatusChip';
import { DEFAULT_BORDER } from 'src/constants';
import { GET_SALES_INVOICES } from 'src/graphql/queries/getSalesInvoices.query';
import {
    filteredSalesInvoicesTableData,
    FilterOptionsType,
    updateTableFilters,
} from 'src/utils/tableUtils';

interface ColumnType {
    accessor?: string;
    heading: string;
    type?: 'text' | 'element' | 'action';
}

const columns: ColumnType[] = [
    {
        accessor: 'invoiceNumber',
        heading: 'Invoice Number',
    },
    {
        accessor: 'merchant.displayName',
        heading: 'Merchant Name',
    },
    {
        accessor: 'merchant.merchantAccountNumber',
        heading: 'Merchant Account Number',
    },
    {
        accessor: 'createdAt',
        heading: 'Date Added',
    },
    {
        accessor: 'status',
        heading: 'Status',
        type: 'element',
    },
];

const ViewSalesInvoicesPage = () => {
    const [tableFilters, setTableFilters] = useState({});
    const theme = useTheme();

    const { data, loading } = useQuery(GET_SALES_INVOICES, {
        fetchPolicy: 'cache-and-network',
        onError: () => {
            return showErrorToast('There was an error retrieving the sales invoices');
        },
    });

    const tableData = useMemo(() => {
        const invoices = data?.getSalesInvoices ?? [];
        return filteredSalesInvoicesTableData(invoices, tableFilters)
            .map(({ status, createdAt, ...rest }) => {
                return {
                    ...rest,
                    status: <StatusChip status={status} type="salesInvoice" />,
                    createdAt,
                };
            })
            .reverse();
    }, [data, tableFilters]);

    const filterOptions = () => {
        const invoices = data?.getSalesInvoices ?? [];
        return Object.fromEntries([
            [
                'Invoice Number',
                Array.from(
                    new Set(
                        invoices
                            .map(({ invoiceNumber }) => {
                                return invoiceNumber;
                            })
                            .filter(Boolean)
                    )
                ).map((invoiceNumber) => {
                    return {
                        value: invoiceNumber,
                        label: invoiceNumber,
                    };
                }),
            ],
            [
                'Merchant Account Number',
                Array.from(
                    new Set(
                        invoices
                            .map(({ merchant }) => {
                                return merchant.merchantAccountNumber;
                            })
                            .filter(Boolean)
                    )
                ).map((merchantAccountNumber) => {
                    return {
                        value: merchantAccountNumber,
                        label: merchantAccountNumber,
                    };
                }),
            ],
            [
                'Merchant Name',
                Array.from(
                    new Set(
                        invoices.map(({ merchant }) => {
                            return merchant.displayName;
                        })
                    )
                )
                    .filter((displayName) => {
                        return displayName;
                    })
                    .map((displayName) => {
                        return {
                            value: displayName,
                            label: displayName,
                        };
                    }),
            ],
            [
                'Status',
                Array.from(
                    new Set(
                        invoices.map(({ status }) => {
                            return status;
                        })
                    )
                )
                    .filter((status) => {
                        return status;
                    })
                    .map((status: string) => {
                        return {
                            value: '',
                            label: status,
                        };
                    }),
            ],
            ['Date', []],
        ]) as FilterOptionsType;
    };

    return (
        <Formik initialValues={{ searchTerm: '', table_filters: '' }} onSubmit={() => {}}>
            {() => {
                return (
                    <>
                        <MetaTags title="Sales Invoices" description="Sales Invoices Page" />

                        <Grid container sx={{ gap: '24px' }}>
                            <Grid
                                item
                                xs={12}
                                sx={{
                                    ...DEFAULT_BORDER,
                                    height: '116px',
                                    backgroundColor: theme.palette.background.default,
                                    boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                                    padding: '8px 24px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '24px',
                                        fontWeight: 700,
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    Sales Invoices
                                </Typography>
                                <Typography>Add and View Sales Invoices as necessary</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                {loading ? (
                                    <Loader />
                                ) : (
                                    <TableList
                                        data={tableData}
                                        headers={columns.map(({ accessor, heading }) => {
                                            return {
                                                id: accessor,
                                                label: heading,
                                            };
                                        })}
                                        addRecordSecondButtonText="Add Sales Invoice"
                                        handleViewRecord={({ id }) => {
                                            return navigate(`/finance/sales-invoice/${id}`);
                                        }}
                                        addSecondRecordCallBack={() => {
                                            return navigate('/finance/sales-invoices/add');
                                        }}
                                        enableFilter
                                        filterOptions={filterOptions()}
                                        updateTableFilters={(
                                            filter: string,
                                            newFilterValue: string
                                        ) => {
                                            return setTableFilters((prevFilters) => {
                                                return updateTableFilters(
                                                    prevFilters,
                                                    newFilterValue,
                                                    filter
                                                );
                                            });
                                        }}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </>
                );
            }}
        </Formik>
    );
};

export default ViewSalesInvoicesPage;
